<script setup>
/* global defineProps, defineEmits */
import { computed, ref, watch } from "vue";
import PresenceService from "@/api/services/newPresencia.service";
import PresenceCheckChanges from "@/components/shared/Presence/PresenceCheckChanges.vue";
import SharedElementPagination from "@/components/shared/SharedElementPagination.vue";
import { useUiStore } from "@/app/stores/uiStore";
import notification from "@/utils/plugins/NotificationService";
import { workRecordRequestApiError } from "@/utils/presenceAbsence";
import {
  isResolvedPresenceRequest,
  presenceRequestKey,
  sortPresenceChangeDays,
} from "@/utils/presenceChanges";

const props = defineProps({
  isAccessedAdmin: Boolean,
  presesenceChanges: { type: Array, default: () => [] },
});
const emit = defineEmits(["close", "finish"]);
const uiStore = useUiStore();
const saving = ref(false);
const decisions = ref({});
const page = ref(1);
const pageSize = ref(10);
const expandedDay = ref(null);
const daysList = ref(null);
const presesenceChanges = computed(() => props.presesenceChanges || []);
const orderedDays = computed(() =>
  sortPresenceChangeDays(presesenceChanges.value),
);
const requestsFor = (day) => day.requests?.workRecordRequests || [];
const pendingRequests = computed(() =>
  presesenceChanges.value.flatMap((day) =>
    requestsFor(day).filter((request) => !isResolvedPresenceRequest(request)),
  ),
);
const isHistory = computed(
  () =>
    pendingRequests.value.length === 0 &&
    presesenceChanges.value.some((day) => requestsFor(day).length > 0),
);
const subtitle = computed(() =>
  isHistory.value
    ? "Histórico de Cambios"
    : props.isAccessedAdmin
      ? "Revisar Cambios Solicitados"
      : "Cambios Pendientes",
);
const visibleDays = computed(() =>
  orderedDays.value.slice(
    (page.value - 1) * pageSize.value,
    page.value * pageSize.value,
  ),
);
const canReview = computed(
  () => props.isAccessedAdmin && pendingRequests.value.length > 0,
);
const closeSeeChanges = () => {
  if (!saving.value) emit("close");
};
const setDecision = ({ id, accepted }) => {
  if (props.isAccessedAdmin && !saving.value) decisions.value[id] = accepted;
};
const setDayDecision = (day, accepted) => {
  if (!props.isAccessedAdmin || saving.value || typeof accepted !== "boolean")
    return;
  const selectedDay = presesenceChanges.value.find((item) => item.day === day);
  if (!selectedDay) return;
  for (const request of requestsFor(selectedDay)) {
    if (!isResolvedPresenceRequest(request)) {
      decisions.value[presenceRequestKey(request)] = accepted;
    }
  }
};
const toggleDay = (day) => {
  expandedDay.value = expandedDay.value === day ? null : day;
};

const resetDayScroll = () => {
  if (daysList.value) daysList.value.scrollTop = 0;
};
watch([page, pageSize], () => {
  expandedDay.value = null;
  resetDayScroll();
});
watch(pageSize, () => {
  page.value = 1;
});
watch(
  () => props.presesenceChanges,
  () => {
    page.value = 1;
    expandedDay.value = null;
    resetDayScroll();
  },
  { immediate: true, deep: true },
);

const clickedCheckChanges = async () => {
  if (!canReview.value || saving.value) return;
  const items = presesenceChanges.value.flatMap((day) =>
    requestsFor(day)
      .filter((request) => !isResolvedPresenceRequest(request))
      .map((request) => ({
        id: request.id,
        action:
          (decisions.value[presenceRequestKey(request)] ??
          day.isAccepted !== false)
            ? "approve"
            : "reject",
      })),
  );
  if (!items.length) return;
  saving.value = true;
  uiStore.startLoading();
  try {
    await PresenceService.batchResolveWorkRecordsRequestAdmin({ items });
    emit("finish");
  } catch (error) {
    const { message } = workRecordRequestApiError(error);
    notification.addNotification({ type: "error", message, duration: 8000 });
  } finally {
    saving.value = false;
    uiStore.stopLoading();
  }
};
</script>

<template>
  <div class="presence-changes" :aria-busy="saving">
    <div class="presence-changes__sticky-header">
      <header class="presence-changes__header">
        <h2 class="presence-changes__title">
          <span aria-hidden="true">*</span>Presencia
        </h2>
        <button
          type="button"
          class="presence-changes__close"
          aria-label="Cerrar cambios de presencia"
          :disabled="saving"
          @click="closeSeeChanges"
        >
          <img src="@/assets/images/aspa_blue_small_entrada.png" alt="" />
        </button>
      </header>
      <h3 class="presence-changes__subtitle">
        <svg viewBox="0 0 28 32" aria-hidden="true">
          <path d="M11 2h6M14 2v4M23 7l2 2M14 11v9l4 2" />
          <circle cx="14" cy="19" r="11" />
        </svg>
        <span>{{ subtitle }}</span>
      </h3>
    </div>
    <div
      ref="daysList"
      class="presence-changes__days"
      role="region"
      aria-label="Días de fichajes"
      tabindex="0"
    >
      <div
        v-if="!presesenceChanges.length"
        class="presence-changes__empty"
        role="status"
      >
        <svg viewBox="0 0 48 48" aria-hidden="true">
          <circle cx="24" cy="24" r="19" />
          <path d="m15 24 6 6 12-13" />
        </svg>
        <h4>No hay solicitudes para revisar</h4>
        <p>Los cambios de fichajes aparecerán aquí.</p>
      </div>
      <PresenceCheckChanges
        v-for="item in visibleDays"
        :key="item.day"
        :day="item.day"
        :model-value="item.isAccepted !== false"
        :requests="item.requests"
        :admin="isAccessedAdmin"
        :expanded="expandedDay === item.day"
        :decisions="decisions"
        :busy="saving"
        @toggle="toggleDay(item.day)"
        @decision="setDecision"
        @day-decision="setDayDecision(item.day, $event)"
      />
    </div>
    <footer class="presence-changes__footer">
      <ul
        v-if="!isHistory && presesenceChanges.length"
        class="presence-changes__legend"
        aria-label="Tipos de cambio"
      >
        <li>
          <span class="presence-changes__dot is-new" aria-hidden="true"></span
          >Nuevo
        </li>
        <li>
          <span
            class="presence-changes__dot is-modification"
            aria-hidden="true"
          ></span
          >Modificado
        </li>
        <li>
          <span
            class="presence-changes__dot is-cancelled"
            aria-hidden="true"
          ></span
          >Cancelado
        </li>
      </ul>
      <div class="presence-changes__footer-controls">
        <SharedElementPagination
          v-model:current-page="page"
          v-model:page-size="pageSize"
          :total="presesenceChanges.length"
          :page-size-options="[10, 20, 50]"
          :pager-count="3"
          size="small"
          :disabled="saving"
          class="presence-changes__pagination"
        />
        <div
          class="presence-changes__actions"
          :class="{ 'is-readonly': !canReview }"
        >
          <button
            type="button"
            class="btn btn_yellow"
            :disabled="saving"
            @click="closeSeeChanges"
          >
            Cerrar
          </button>
          <button
            v-if="canReview"
            type="button"
            class="btn btn_red"
            aria-label="Guardar la revisión de todas las solicitudes pendientes"
            title="Guardar la revisión de todas las solicitudes pendientes"
            :disabled="saving"
            @click="clickedCheckChanges"
          >
            {{ saving ? "Guardando…" : "Aceptar" }}
          </button>
        </div>
      </div>
    </footer>
  </div>
</template>

<style lang="scss" scoped>
.presence-changes {
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: calc(100dvh - 72px);
  min-width: 0;
  color: #17182d;
  background: $bg-light;
}
.presence-changes__sticky-header {
  flex: 0 0 auto;
  // The header sits outside the scroll area; contain its subtitle margins.
  display: flow-root;
  background: $bg-light;
}
.presence-changes__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.presence-changes__title {
  display: flex;
  align-items: baseline;
  margin: 0;
  color: #f9156f;
  font-size: 42px;
  line-height: 1.05;
  font-weight: 900;
  letter-spacing: -0.045em;
}
.presence-changes__title span {
  align-self: flex-start;
  font-size: 44px;
  margin-right: 2px;
}
.presence-changes__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  padding: 5px;
  margin: 0;
  border: 0;
  border-radius: 10px;
  background: transparent;
}
.presence-changes__close img {
  display: block;
  width: 28px;
  height: 28px;
  object-fit: contain;
}
.presence-changes__close:hover {
  background: #e6dfff;
}
.presence-changes__close:focus-visible,
.presence-changes__actions .btn:focus-visible {
  outline: 3px solid #409eff;
  outline-offset: 2px;
}
.presence-changes__subtitle {
  display: flex;
  align-items: center;
  gap: 9px;
  margin: 5px 0 14px;
  color: $bg-blue;
  font-size: 28px;
  line-height: 1.2;
  font-weight: 800;
  letter-spacing: -0.025em;
}
.presence-changes__subtitle > span {
  font: inherit;
}
.presence-changes__subtitle svg {
  width: 26px;
  height: 30px;
  flex: 0 0 26px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.6;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.presence-changes__days {
  display: flex;
  flex-direction: column;
  flex: 0 1 auto;
  min-height: 0;
  gap: 8px;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
  }
}
.presence-changes__days:focus-visible {
  outline: 2px solid #409eff;
  outline-offset: -2px;
}
.presence-changes__days > :deep(.presence-change-day) {
  flex-shrink: 0;
}
.presence-changes__footer {
  flex: 0 0 auto;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #ded8ff;
}
.presence-changes__footer-controls {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
}
.presence-changes__legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 22px;
  margin: 0 0 12px;
  padding: 0;
  list-style: none;
}
.presence-changes__legend li {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
}
.presence-changes__dot {
  display: inline-block;
  flex: 0 0 11px;
  width: 11px;
  height: 11px;
  border-radius: 50%;
}
.presence-changes__dot.is-new {
  background: #80c94b;
}
.presence-changes__dot.is-modification {
  background: #f1d92c;
}
.presence-changes__dot.is-cancelled {
  background: #fa1837;
}
.presence-changes__actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
}
.presence-changes__actions .btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 190px;
  max-width: 100%;
  min-height: 44px;
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  border-radius: 24px;
}
.presence-changes__actions .btn_red {
  color: #fff;
}
.presence-changes__actions.is-readonly {
  justify-content: center;
}
.presence-changes__actions :disabled,
.presence-changes__close:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
.presence-changes__pagination {
  justify-content: center;
  margin: 0;
  min-width: 0;
  max-width: 100%;
}
.presence-changes__footer .presence-changes__pagination :deep(.btn-prev),
.presence-changes__footer .presence-changes__pagination :deep(.btn-next) {
  margin: 0 !important;
}
.presence-changes__empty {
  padding: 30px 8px;
  text-align: center;
}
.presence-changes__empty svg {
  width: 52px;
  height: 52px;
  margin-bottom: 12px;
  fill: none;
  stroke: $bg-blue;
  stroke-width: 2;
}
.presence-changes__empty h4 {
  color: $bg-blue;
  font-size: 20px;
  font-weight: 800;
}
.presence-changes__empty p {
  margin: 8px 0 0;
  color: #606174;
}
@media (min-width: 768px) {
  .presence-changes__footer-controls {
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 16px;
  }
  .presence-changes__footer-controls .presence-changes__pagination {
    flex: 0 1 auto;
    width: auto;
  }
  .presence-changes__footer .presence-changes__pagination :deep(.el-pager) {
    padding: 0 2px !important;
  }
  .presence-changes__footer .presence-changes__pagination :deep(.el-pager li) {
    min-width: 22px;
  }
  .presence-changes__footer
    .presence-changes__pagination
    :deep(.el-pagination__sizes) {
    flex: 0 0 96px;
    width: 96px !important;
    margin-left: 8px !important;
  }
  .presence-changes__footer
    .presence-changes__pagination
    :deep(.el-pagination__sizes .el-select) {
    width: 96px !important;
  }
  .presence-changes__actions {
    flex: 0 0 auto;
    gap: 12px;
  }
  .presence-changes__actions .btn {
    width: 144px;
  }
}
@media (max-width: 767px) {
  .presence-changes {
    max-height: calc(100dvh - 56px);
  }
  .presence-changes__title {
    font-size: 34px;
  }
  .presence-changes__title span {
    font-size: 37px;
  }
  .presence-changes__subtitle {
    align-items: flex-start;
    gap: 7px;
    margin: 6px 0 14px;
    font-size: 22px;
  }
  .presence-changes__subtitle svg {
    width: 23px;
    height: 27px;
    flex-basis: 23px;
  }
  .presence-changes__legend {
    justify-content: space-between;
    gap: 8px;
  }
  .presence-changes__legend li {
    font-size: 13px;
    font-weight: 500;
    line-height: 1.4;
  }
  .presence-changes__footer .presence-changes__pagination :deep(.el-pager li) {
    min-width: 21px;
  }
  .presence-changes__actions {
    gap: 10px;
  }
  .presence-changes__actions .btn {
    flex: 1 1 0;
    width: auto;
    min-width: 0;
    min-height: 44px;
  }
  .presence-changes__actions.is-readonly .btn {
    flex: 0 1 190px;
  }
}
@media (max-width: 359px) {
  .presence-changes__title {
    font-size: 30px;
  }
  .presence-changes__subtitle {
    font-size: 20px;
  }
  .presence-changes__legend li {
    gap: 5px;
    font-size: 12px;
  }
  .presence-changes__footer .presence-changes__pagination :deep(.el-pager) {
    padding: 0 !important;
  }
  .presence-changes__footer .presence-changes__pagination :deep(.el-pager li) {
    min-width: 18px;
  }
  .presence-changes__footer
    .presence-changes__pagination
    :deep(.el-pager li.is-active) {
    min-width: 22px;
  }
  .presence-changes__footer .presence-changes__pagination :deep(.btn-prev),
  .presence-changes__footer .presence-changes__pagination :deep(.btn-next) {
    flex-basis: 24px;
    min-width: 24px !important;
    width: 24px !important;
  }
  .presence-changes__footer
    .presence-changes__pagination
    :deep(.el-pagination__sizes) {
    flex-basis: 64px;
    width: 64px !important;
    margin-left: 4px !important;
  }
  .presence-changes__footer
    .presence-changes__pagination
    :deep(.el-pagination__sizes .el-select) {
    width: 64px !important;
  }
}
@media (max-width: 767px) and (max-height: 500px) {
  .presence-changes {
    max-height: calc(100dvh - 48px);
  }
  .presence-changes__subtitle {
    margin: 4px 0 8px;
    font-size: 20px;
  }
  .presence-changes__footer {
    margin-top: 8px;
    padding-top: 8px;
  }
  .presence-changes__legend {
    margin-bottom: 8px;
  }
  .presence-changes__footer-controls {
    gap: 8px;
  }
}
</style>

<style lang="scss">
// Keep the overlay stationary; only the days inside the dialog scroll.
.presence-changes-overlay .el-overlay-dialog {
  overflow: hidden;
  overscroll-behavior: none;
}

// Both entry points share a dialog shell; avoid legacy mobile header overrides.
.el-dialog.presence-changes-dialog {
  display: flex;
  flex-direction: column;
  // align-center makes the overlay flex; fixed vertical margins must not stretch the dialog.
  align-self: center;
  height: auto;
  min-height: 0;
  width: min(760px, calc(100vw - 32px));
  max-width: none;
  max-height: calc(100dvh - 32px);
  overflow: hidden;
  margin: 16px auto;
  padding: 20px;
  border-radius: 20px;
  background: linear-gradient(135deg, #fcfaff 0%, #f1ebff 65%, #eae2ff 100%);
  box-shadow:
    0 18px 60px rgba(31, 24, 82, 0.26),
    0 3px 9px rgba(31, 24, 82, 0.12);
  .el-dialog__header {
    display: none;
  }
  .el-dialog__body {
    display: flex;
    flex-direction: column;
    flex: 0 1 auto;
    // Override the legacy global min-height and overflow hidden !important.
    min-height: 0 !important;
    padding: 0;
    overflow-x: hidden !important;
    overflow-y: hidden !important;
  }
}
@media (max-width: 767px) {
  .el-dialog.presence-changes-dialog {
    width: calc(100vw - 24px);
    max-height: calc(100dvh - 24px);
    margin: 12px auto;
    padding: 16px 12px;
    border-radius: 20px;
  }
}
@media (max-width: 767px) and (max-height: 500px) {
  .el-dialog.presence-changes-dialog {
    padding-top: 12px;
    padding-bottom: 12px;
  }
}
</style>
